import { FC, useEffect, useState } from "react";

import { Box, useMultiStyleConfig } from "@chakra-ui/react";

import { FullWidthVStack } from "@components/common";
import Loader from "@components/Loader";
import responsiveStyle from "@lib/theme/responsiveStyle";

import { OrderFields } from "../../constants";
import useOrderStorage from "../../hooks/storage/useOrderStorage";
import useOrderLabelDownload from "../../hooks/success/useOrderLabelDownload";
import type { TPaymentStatus } from "../../types";
import OrderDescription from "./OrderDescription";
import OrderInfo from "./OrderInfo";
import PurchaseDataLayerEvent from "./PurchaseDataLayerEvent";
import PurchaseGTMEvent from "./PurchaseGTMEvent";
import SuccessFooter from "./SuccessFooter";
import SuccessMessage from "./SuccessMessage";

type TSuccessProps = {
  paymentStatus: TPaymentStatus;
};

const Success: FC<TSuccessProps> = ({ paymentStatus }) => {
  const [affiliateReturnUrl, setAffiliateReturnUrl] = useState<string>();

  const styles = useMultiStyleConfig("Order", {});

  const { shipmentId, parcelNo } = paymentStatus;

  const { getData, clearData } = useOrderStorage();
  useEffect(() => {
    // Storing affiliate return url before clearing order data
    if (!affiliateReturnUrl) {
      const order = getData();
      setAffiliateReturnUrl(order?.[OrderFields.AFFILIATE_RETURN_URL] || undefined);
    }

    // When payment is successfull, need to clear stored order data
    clearData();
  }, [affiliateReturnUrl, clearData, getData]);

  const { isLoading, downloadOrderLabel } = useOrderLabelDownload();

  const handleLabelDownload = async () => {
    await downloadOrderLabel(shipmentId, parcelNo || undefined);

    if (affiliateReturnUrl) {
      window.location.href = affiliateReturnUrl;
    }
  };

  return (
    <Box sx={styles.pageContent}>
      <Loader sx={{ height: "100%" }} isLoading={isLoading}>
        <FullWidthVStack spacing="1.5rem" height="100%">
          <FullWidthVStack spacing="1.5rem" flex={responsiveStyle({ mobile: "1", desktop: "initial" })}>
            <SuccessMessage />
            <OrderDescription paymentStatus={paymentStatus} onDownloadLabel={handleLabelDownload} />
            <OrderInfo paymentStatus={paymentStatus} />
          </FullWidthVStack>
          <SuccessFooter />
          <PurchaseGTMEvent paymentStatus={paymentStatus} />
          <PurchaseDataLayerEvent paymentStatus={paymentStatus} />
        </FullWidthVStack>
      </Loader>
    </Box>
  );
};
export default Success;
